import { useMemo, useCallback } from 'react';
import useAuth from './useAuth';
import { useActiveAccountOptional } from './useActiveAccount';
import {
  canAccessSection,
  parseProfileAttribute,
  parsePermissionsAttribute,
  resolveSectionIdFromPath,
  isEnforcedRole,
  normalizeAccessValue,
  INBOX_COMPAT_DEFAULTS,
} from '../constants/permissions';

const usePermissions = () => {
  const {
    user,
    backoffice: authBackoffice,
    platformAdmin: authPlatformAdmin,
    canAccessAllAccounts,
  } = useAuth();
  const account = useActiveAccountOptional();

  const legacy = useMemo(() => {
    const rawProfileValue = user?.attributes?.profile || 'user';
    const parsedProfile = parseProfileAttribute(rawProfileValue);
    const customOverrides = parsePermissionsAttribute(
      user?.attributes?.['custom:permissions']
    );
    const overrides = {
      ...parsedProfile.overrides,
      ...customOverrides,
    };
    return {
      rawProfile: rawProfileValue,
      role: parsedProfile.role,
      overrides,
    };
  }, [user?.attributes]);

  const role = account ? account.role : legacy.role;
  const rawRole = account ? account.role : legacy.rawProfile;
  const backoffice = Boolean(account?.backoffice ?? authBackoffice);
  const platformAdmin = Boolean(account?.platformAdmin ?? authPlatformAdmin);
  const permissions = useMemo(
    () => (account ? { ...(account.permissions || {}), ...legacy.overrides } : legacy.overrides),
    [account, legacy.overrides]
  );
  const overrides = permissions;

  const can = useCallback(
    (sectionId) =>
      canAccessSection({
        sectionId,
        canAccessAllAccounts,
        role,
        permissions,
      }),
    [canAccessAllAccounts, role, permissions]
  );

  const canPath = useCallback(
    (pathname) => {
      const sectionId = resolveSectionIdFromPath(pathname);
      if (!sectionId) return true;
      return can(sectionId);
    },
    [can]
  );

  const filterViewsByPermission = useCallback(
    (views) => {
      if (!Array.isArray(views)) return [];
      return views.filter((view) => {
        const path = view?.path;
        if (!path) return false;
        const normalized = path.startsWith('/') ? path : `/${path}`;
        return canPath(normalized);
      });
    },
    [canPath]
  );

  const isAccountAdmin = account
    ? Boolean(
        account.isAccountAdmin ||
          account.role === 'admin' ||
          account.backoffice ||
          canAccessAllAccounts
      )
    : role === 'admin' || Boolean(canAccessAllAccounts);
  const isAdmin = isAccountAdmin;
  const canManageUsers = can('platform.users');
  // Rol con restricciones que se hacen cumplir (p. ej. Asesor). Un administrador nunca lo es.
  const restrictedRole = !isAccountAdmin && isEnforcedRole(role);

  // Permisos de la bandeja. Valen para TODOS los roles (el backend los hace cumplir). Si el mapa
  // no trae el permiso, rige lo que ya tenía un "Usuario" y no "denegado": así un rol guardado
  // antes de que existieran no pierde la bandeja ni Cerradas, ni depende del orden de despliegue.
  const canInbox = useCallback(
    (sectionId) => {
      if (isAccountAdmin) return true;
      if (Object.prototype.hasOwnProperty.call(permissions, sectionId)) {
        return normalizeAccessValue(permissions[sectionId]);
      }
      return INBOX_COMPAT_DEFAULTS[sectionId] === true;
    },
    [isAccountAdmin, permissions]
  );

  return {
    role,
    rawRole,
    overrides,
    permissions,
    can,
    canInbox,
    canPath,
    isAdmin,
    isAccountAdmin,
    restrictedRole,
    canManageUsers,
    filterViewsByPermission,
    resolveSectionIdFromPath,
    backoffice,
    platformAdmin,
  };
};

export default usePermissions;
